import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Bell, Calendar, MessageCircle, CreditCard, Settings } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import { useTranslation } from '../i18n';

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    fetchSetupStatus();
  }, []);

  const fetchSetupStatus = async () => {
    try {
      const response = await apiClient.get('/user/setup-status');
      setSetupStatus(response.data);
    } catch (err) {
      console.error('Error fetching setup status:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const [statsResponse, conversationsResponse] = await Promise.all([
        apiClient.get('/stats').catch(err => {
          if (err.response?.status === 402) return { data: null };
          throw err;
        }),
        apiClient.get('/conversations?limit=3').catch(err => {
          if (err.response?.status === 402) return { data: [] };
          throw err;
        })
      ]);
      setStats(statsResponse.data);
      setRecentConversations(Array.isArray(conversationsResponse.data) ? conversationsResponse.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load dashboard data', t));
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </>
    );
  }

  // Show error banner if there's an error
  const showError = !!error;

  const quickActions = [
    {
      title: t('tasks'),
      icon: CheckSquare,
      path: '/tasks',
      color: '#2563eb',
      bgColor: '#dbeafe',
      description: t('manageTasks')
    },
    {
      title: t('reminders'),
      icon: Bell,
      path: '/reminders',
      color: '#059669',
      bgColor: '#d1fae5',
      description: t('viewReminders')
    },
    {
      title: t('calendar'),
      icon: Calendar,
      path: '/calendar',
      color: '#d97706',
      bgColor: '#fef3c7',
      description: t('checkSchedule')
    },
    {
      title: t('conversations'),
      icon: MessageCircle,
      path: '/chat',
      color: '#7c3aed',
      bgColor: '#ede9fe',
      description: t('chatHistory')
    },
    {
      title: t('integrations'),
      icon: Settings,
      path: '/config',
      color: '#6b7280',
      bgColor: '#f3f4f6',
      description: t('integrationsDesc')
    },
    {
      title: t('subscription.title'),
      icon: CreditCard,
      path: '/config?tab=subscription',
      color: '#dc2626',
      bgColor: '#fee2e2',
      description: t('subscription.manageSubscription')
    }
  ];

  return (
    <>
      <Navigation />
      
      {/* General Error Banner */}
      {showError && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '1rem',
          margin: '1rem',
          color: '#991b1b'
        }}>
          {error}
        </div>
      )}
      
      <div className="container safe-area-bottom">
        {/* WhatsApp Setup Banner */}
        {setupStatus && !setupStatus.whatsapp_verified && (
          <div style={{
            background: '#dcfce7',
            border: '2px solid #22c55e',
            borderRadius: '12px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '2rem' }}>💬</div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
                {t('whatsapp.setupTitle')}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
                {t('whatsapp.setupDescription')}
              </div>
            </div>
            <Link
              to="/config?tab=platforms"
              style={{
                padding: '0.625rem 1.25rem',
                background: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'background 0.2s',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#16a34a'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#22c55e'}
            >
              {t('whatsapp.setupButton')}
            </Link>
          </div>
        )}
        
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="text-lg" style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
            {setupStatus?.first_name ? `${t('welcomeBack')}, ${setupStatus.first_name}!` : t('welcomeBack')}
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>
            {t('productivityOverview')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <Link to="/tasks" className="stat-card" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="stat-number">{stats?.active_tasks || 0}</div>
            <div className="stat-label">{t('activeTasks')}</div>
          </Link>
          <Link to="/reminders" className="stat-card" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="stat-number">{stats?.active_reminders || 0}</div>
            <div className="stat-label">{t('reminders')}</div>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">{t('quickActions')}</h2>
          </div>
          <div className="grid-mobile">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.path}
                  to={action.path}
                  className="touch-target"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e0e0e0',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'all 0.2s',
                    minHeight: '60px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.borderColor = action.color;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  <div
                    style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      backgroundColor: action.bgColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '1rem',
                      flexShrink: 0
                    }}
                  >
                    <Icon size={20} color={action.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-base" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      {action.title}
                    </div>
                    <div className="text-sm" style={{ color: '#666' }}>
                      {action.description}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">{t('recentActivity')}</h2>
            <Link to="/conversations" className="btn btn-outline" style={{ fontSize: '0.875rem' }}>
              {t('viewAll')}
            </Link>
          </div>
          {recentConversations.length === 0 ? (
            <div style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
              <MessageCircle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>{t('recentConversationsText')}</p>
              <Link to="/conversations" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                {t('viewConversations')}
              </Link>
            </div>
          ) : (
            <ul className="list">
              {recentConversations.map((conversation) => (
                <li key={conversation.message_id} className="list-item">
                  <div className="list-item-content">
                    <div className="list-item-title" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      {conversation.message.length > 60 ? `${conversation.message.substring(0, 60)}...` : conversation.message}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{conversation.source === 'telegram' ? '📱' : conversation.source === 'whatsapp' ? '💬' : conversation.source}</span>
                      <span>{new Date(conversation.timestamp).toLocaleDateString()}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;